import { Info } from "lucide-react";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { ptBR } from "date-fns/locale";
import { Controller, useFormContext } from "react-hook-form";
import { InputMask } from "@react-input/mask";
import { CalendarDotsIcon } from "@/modules/common/assets/icons";
import { cn } from "@/libs/cn";
import { dayjsExtension } from "@/utils/days";
import Button from "../Button";
import Tooltip from "../Tooltip";

const DATE_MASK_REPLACEMENT = { _: /\d/ };
const ISO_FORMAT = "YYYY-MM-DD";
const DISPLAY_FORMAT = "DD/MM/YYYY";
const REACT_DATEPICKER_DATE_FORMAT = "dd/MM/yyyy";
const YEAR_RANGE = 200;
const YEAR_START_OFFSET = -100;

const MONTHS = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

interface DateInputProps {
  name?: string;
  label?: string;
  value?: string;
  disabled?: boolean;
  onChange?: (value: string) => void;
  min?: string;
  max?: string;
  placeholder?: string;
  markAsRequired?: boolean;
  infoMessage?: string;
  inputClassName?: string;
}

const DateInput = ({
  name,
  label,
  value,
  disabled = false,
  onChange,
  min,
  max,
  placeholder = "Selecione uma data",
  markAsRequired,
  infoMessage,
  inputClassName,
}: DateInputProps) => {
  const form = useFormContext();
  const [open, setOpen] = useState(false);
  const [localValue, setLocalValue] = useState("");

  const minimalDate = min ? dayjsExtension(min).format(ISO_FORMAT) : undefined;
  const maximumDate = max ? dayjsExtension(max).format(ISO_FORMAT) : undefined;

  useEffect(() => {
    const currentValue = form && name ? form.getValues(name) : value;

    if (currentValue) {
      const current = dayjsExtension(currentValue, ISO_FORMAT);
      if (minimalDate && current.isBefore(dayjsExtension(minimalDate, ISO_FORMAT))) {
        if (form && name) form.setValue(name, minimalDate, { shouldValidate: true });
        if (onChange) onChange(minimalDate);
      } else if (maximumDate && current.isAfter(dayjsExtension(maximumDate, ISO_FORMAT))) {
        if (form && name) form.setValue(name, maximumDate, { shouldValidate: true });
        if (onChange) onChange(maximumDate);
      }
    }
  }, [minimalDate, maximumDate, name, value]);

  const popperModifiers = [
    { name: "flip", options: { enabled: true }, fn: () => ({}) },
    { name: "preventOverflow", options: { enabled: false }, fn: () => ({}) },
    { name: "offset", options: { offset: [0, 8] }, fn: () => ({}) },
  ];

  const parseManualInput = (input: string): string => {
    if (!input || input.length < 10 || input.includes("_")) return "";

    const parsedDate = dayjsExtension(input, DISPLAY_FORMAT, true);
    return parsedDate.isValid() ? parsedDate.format(ISO_FORMAT) : "";
  };

  const handleChange = (date: Date | null, fieldOnChange?: (value: string) => void) => {
    const formatted = date ? dayjsExtension(date).format(ISO_FORMAT) : "";
    if (fieldOnChange) fieldOnChange(formatted);
    if (onChange) onChange(formatted);
    setLocalValue("");
    setOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, fieldOnChange?: (value: string) => void) => {
    const value = e.target.value;
    setLocalValue(value);

    const parsed = parseManualInput(value);
    if (parsed) {
      const parsedDate = dayjsExtension(parsed, ISO_FORMAT);
      if (minimalDate && parsedDate.isBefore(dayjsExtension(minimalDate, ISO_FORMAT))) return;
      if (maximumDate && parsedDate.isAfter(dayjsExtension(maximumDate, ISO_FORMAT))) return;
      if (fieldOnChange) fieldOnChange(parsed);
      if (onChange) onChange(parsed);
    }
  };

  const handleInputBlur = () => {
    setLocalValue("");
  };

  const handleInputFocus = (inputValue: string) => {
    if (disabled) return;
    const parsedValue = inputValue ? dayjsExtension(inputValue, ISO_FORMAT) : null;
    const displayValue = parsedValue?.isValid() ? parsedValue.format(DISPLAY_FORMAT) : "";
    if (displayValue) {
      setLocalValue(displayValue);
    }
  };

  const renderInput = (inputValue: string, fieldOnChange?: (value: string) => void) => {
    const parsedValue = inputValue ? dayjsExtension(inputValue, ISO_FORMAT).toDate() : null;
    const displayValue = inputValue && !localValue ? dayjsExtension(inputValue, ISO_FORMAT).format(DISPLAY_FORMAT) : localValue;
    const minDate = minimalDate ? dayjsExtension(minimalDate, ISO_FORMAT).toDate() : undefined;
    const maxDate = maximumDate ? dayjsExtension(maximumDate, ISO_FORMAT).toDate() : undefined;

    return (
      <>
        <DatePicker
          disabled={disabled}
          selected={parsedValue}
          onChange={(date: Date | null) => handleChange(date, fieldOnChange)}
          dateFormat={REACT_DATEPICKER_DATE_FORMAT}
          popperPlacement="bottom-start"
          popperModifiers={popperModifiers}
          placeholderText={placeholder}
          locale={ptBR}
          open={open}
          onClickOutside={() => setOpen(false)}
          onSelect={() => setOpen(false)}
          minDate={minDate}
          maxDate={maxDate}
          fixedHeight
          popperClassName="date-picker-popper"
          calendarContainer={({ className, children }) => (
            <div className={`${className} overflow-hidden`}>
              {children}
              <div className="flex items-center justify-between p-2 gap-2 border-t border-[#D6DEE9]">
                <Button
                  type="button"
                  size="small"
                  label="Limpar"
                  className="w-full h-8 py-1 text-xs"
                  onClick={() => handleChange(null, fieldOnChange)}
                />
                <Button
                  variant="outlined"
                  type="button"
                  size="small"
                  label="Hoje"
                  className="w-full h-8 py-1 text-xs"
                  onClick={() => handleChange(new Date(), fieldOnChange)}
                />
              </div>
            </div>
          )}
          renderCustomHeader={({ date, decreaseMonth, increaseMonth, changeYear, changeMonth }) => {
            const now = new Date();
            const currentYear = date.getFullYear();
            const currentMonth = date.getMonth();
            const todayValue = dayjsExtension(now).format(ISO_FORMAT);
            const isTodayDisabled = !!((minimalDate && todayValue < minimalDate) || (maximumDate && todayValue > maximumDate));
            const years = Array.from({ length: YEAR_RANGE }, (_, i) => now.getFullYear() + YEAR_START_OFFSET + i);

            return (
              <div className="flex gap-2 p-2 items-center justify-between" onMouseDown={(e) => e.stopPropagation()}>
                <button type="button" onClick={decreaseMonth} className="p-1 hover:bg-gray-100 rounded transition-colors">
                  <svg width="8" height="12" viewBox="0 0 8 12" fill="none">
                    <path d="M7 1L1 6L7 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                <div className="flex gap-2">
                  <select
                    value={currentMonth}
                    onChange={(e) => changeMonth(Number(e.target.value))}
                    className="text-sm border rounded px-2 py-1 outline-none focus:border-primary"
                  >
                    {MONTHS.map((month, index) => (
                      <option key={index} value={index}>
                        {month}
                      </option>
                    ))}
                  </select>

                  <select
                    value={currentYear}
                    onChange={(e) => changeYear(Number(e.target.value))}
                    className="text-sm border rounded px-2 py-1 outline-none focus:border-primary"
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                <button type="button" onClick={increaseMonth} className="p-1 hover:bg-gray-100 rounded transition-colors">
                  <svg width="8" height="12" viewBox="0 0 8 12" fill="none">
                    <path d="M1 1L7 6L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                <Button
                  variant="outlined"
                  label="Hoje"
                  disabled={isTodayDisabled}
                  className="p-0.5 text-xs border-transparent hover:bg-transparent underline w-fit h-fit ml-2"
                  onClick={() => handleChange(now, fieldOnChange)}
                />
              </div>
            );
          }}
          customInput={
            <InputMask
              mask="__/__/____"
              replacement={DATE_MASK_REPLACEMENT}
              value={displayValue}
              onChange={(e) => handleInputChange(e, fieldOnChange)}
              onFocus={() => handleInputFocus(inputValue)}
              onBlur={handleInputBlur}
              placeholder={placeholder}
              disabled={disabled}
              className={cn(
                "w-full p-3 h-11 border rounded-lg text-sm outline-none text-left pr-10 placeholder:text-sm placeholder:font-normal placeholder:text-[#ADB7D3] transition-colors",
                disabled
                  ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed select-none"
                  : cn("bg-white text-gray-900", open ? "border-primary ring-2 ring-primary" : "border-gray-300"),
                inputClassName,
              )}
            />
          }
        />
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((prev) => !prev)}
          className={`absolute right-3 top-1/2 -translate-y-1/2 ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
        >
          <CalendarDotsIcon
            className={`w-5 h-5 transition-colors ${disabled ? "text-gray-300" : open ? "text-primary" : "text-gray-500"}`}
          />
        </button>
      </>
    );
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-start gap-2">
        {label && (
          <label className={`text-sm font-medium transition-colors ${disabled ? "text-gray-400" : "text-gray-900"}`}>
            {label}
            {markAsRequired && <span className="text-red-500"> *</span>}
          </label>
        )}
        {infoMessage && (
          <Tooltip contentClassName="w-[250px]" label={infoMessage} side="right">
            <Info className={`w-4 h-4 transition-colors ${disabled ? "text-gray-300" : "text-ui-text-muted"}`} />
          </Tooltip>
        )}
      </div>
      <div className="relative w-full">
        {form && name ? (
          <Controller
            name={name}
            control={form.control}
            rules={{
              validate: (val) => {
                if (!val) return true;
                if (minimalDate && val < minimalDate) return "Data menor que o mínimo permitido";
                if (maximumDate && val > maximumDate) return "Data maior que o máximo permitido";
                return true;
              },
            }}
            render={({ field }) => renderInput(field.value, field.onChange)}
          />
        ) : (
          renderInput(value || "", onChange)
        )}
      </div>
    </div>
  );
};

export default DateInput;
